import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Pagination,
  Collapse
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import {
  fetchLatestPerPattern,
  fetchCollectedTests,
  fetchTestRuns,
  fetchTestRunById,
  runTestSuite,
  runAllTestSuites,
  cancelTestRun,
} from "../services/testSuites.service";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { statusChip, statusIcon, suiteCell, SummaryCard } from "./shared";

const TEST_MODULES = [
  { name: "Auth",            suites: [{ label: "Unit Tests", pattern: "auth.unit" }, { label: "Integration Tests", pattern: "auth.integration" }] },
  { name: "Clients",         suites: [{ label: "Unit Tests", pattern: "clients.unit" }, { label: "Integration Tests", pattern: "clients.integration" }] },
  { name: "Contacts",        suites: [{ label: "Unit Tests", pattern: "contacts.unit" }, { label: "Integration Tests", pattern: "contacts.integration" }] },
  { name: "Dashboard",       suites: [{ label: "Integration Tests", pattern: "dashboard.integration" }] },
  { name: "Fact Find",       suites: [{ label: "Unit Tests", pattern: "factfind.unit" }, { label: "Integration Tests", pattern: "factfind.integration" }] },
  { name: "Files",           suites: [{ label: "Integration Tests", pattern: "files.integration" }, { label: "Upload Tests", pattern: "files.upload" }] },
  { name: "Knowledge Graph", suites: [{ label: "Unit Tests", pattern: "kg.unit" }, { label: "Integration Tests", pattern: "kg.integration" }] },
  { name: "Meetings",        suites: [{ label: "Unit Tests", pattern: "meetings.unit" }, { label: "Integration Tests", pattern: "meetings.integration" }] },
  { name: "Users",           suites: [{ label: "Unit Tests", pattern: "users.unit" }, { label: "Integration Tests", pattern: "users.integration" }] },
];

const ALL_PATTERNS = TEST_MODULES.flatMap(m => m.suites.map(s => s.pattern));

interface TestEntry {
  name: string;
  status: "idle" | "running" | "passed" | "failed" | "skipped";
  durationMs: number;
  error?: string;
}

interface SuiteState {
  status: "idle" | "running" | "passed" | "failed";
  tests: TestEntry[];
  durationMs: number;
  ranAt: string | null;
  passed?: number;
  failed?: number;
  total?: number;
}

interface RunSummary {
  _id: string;
  runNumber: number;
  triggeredBy: string;
  pattern?: string;
  status: "running" | "passed" | "failed" | "error" | "cancelled";
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  suites?: Array<{ pattern: string; suiteName: string; total: number; passed: number; failed: number }>;
}

const PAGE_LIMIT = 20;

export const UnitTestsPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"tests" | "history">("tests");
  const [expandedRunIds, setExpandedRunIds] = useState<Set<string>>(new Set());
  const [runDetails, setRunDetails] = useState<Record<string, any>>({});
  const [loadingRunDetails, setLoadingRunDetails] = useState<Set<string>>(new Set());

  const [suiteStates, setSuiteStates] = useState<Record<string, SuiteState>>(
    () => Object.fromEntries(
      ALL_PATTERNS.map(p => [p, { status: "idle", tests: [], durationMs: 0, ranAt: null }])
    )
  );
  const [lastRunPatterns, setLastRunPatterns] = useState<string[]>([]);
  const activeRunIdRef = useRef<string | null>(null);

  const [history, setHistory] = useState<RunSummary[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [collapsedSuites, setCollapsedSuites] = useState<Record<string, boolean>>(
    () => Object.fromEntries(ALL_PATTERNS.map(p => [p, true]))
  );
  const [collapsedSuiteKeys, setCollapsedSuiteKeys] = useState<Set<string>>(new Set());

  const toggleSuiteKey = useCallback((key: string) => {
    setCollapsedSuiteKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const [committedSummary, setCommittedSummary] = useState<{
    total: number; passed: number; failed: number; durationMs: number;
  } | null>(null);
  const wasRunningRef = useRef(false);
  const suiteStatesRef = useRef(suiteStates);


  const isAnySuiteRunning = Object.values(suiteStates).some(s => s.status === "running");

  useEffect(() => {
    fetchLatestPerPattern().then((res: any) => {
      if (!res?.results) return;
      const patternsWithResults: string[] = [];
      for (const [pattern, run] of Object.entries(res.results) as any) {
        const suite = run?.suites?.find((s: any) => s.pattern === pattern);
        if (suite) patternsWithResults.push(pattern as string);
      }
      setSuiteStates(prev => {
        const next = { ...prev };
        for (const [pattern, run] of Object.entries(res.results) as any) {
          const suite = run?.suites?.find((s: any) => s.pattern === pattern);
          if (suite) {
            next[pattern] = {
              status: suite.status,
              tests: suite.tests?.length
                ? suite.tests.map((t: any) => ({
                    name: t.name,
                    status: t.status,
                    durationMs: t.durationMs ?? 0,
                    error: t.errorMessage,
                    stack: t.stackTrace,
                  }))
                : (prev[pattern]?.tests ?? []),
              durationMs: suite.durationMs ?? 0,
              ranAt: run.completedAt,
              passed: suite.passed,
              failed: suite.failed,
              total: suite.total,
            };
          }
        }
        return next;
      });
      if (patternsWithResults.length > 0) setLastRunPatterns(patternsWithResults);
      const getSuite = (p: string) => res.results[p]?.suites?.find((s: any) => s.pattern === p);
      const total = patternsWithResults.reduce((sum, p) => sum + (getSuite(p)?.total ?? 0), 0);
      const passed = patternsWithResults.reduce((sum, p) => sum + (getSuite(p)?.passed ?? 0), 0);
      const failed = patternsWithResults.reduce((sum, p) => sum + (getSuite(p)?.failed ?? 0), 0);
      const durationMs = patternsWithResults.reduce((sum, p) => sum + (getSuite(p)?.durationMs ?? 0), 0);
      setCommittedSummary({ total, passed, failed, durationMs });
    }).catch(() => {});
  }, []);



  useEffect(() => {
    fetchCollectedTests().then((res: any) => {
      const allTests: Array<{ name: string; file: string }> = res?.tests ?? [];
      setSuiteStates(prev => {
        const next = { ...prev };
        for (const pattern of ALL_PATTERNS) {
          if (next[pattern].tests.length === 0) {
            const names = allTests
              .filter(t => t.file && t.file.includes(pattern))
              .map(t => ({ name: t.name, status: "idle" as const, durationMs: 0 }));
            if (names.length > 0) {
              next[pattern] = { ...next[pattern], tests: names };
            }
          }
        }
        return next;
      });
    }).catch(() => {});
  }, []);


  const fetchHistory = useCallback(async (p: number) => {
    setLoadingHistory(true);
    try {
      const res: any = await fetchTestRuns(p, PAGE_LIMIT);
      setHistory(res?.runs ?? []);
      setHistoryTotal(res?.total ?? 0);
    } catch {
      // ignore
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const toggleRun = useCallback(async (runId: string) => {
    setExpandedRunIds(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId); else next.add(runId);
      return next;
    });
    if (!runDetails[runId] && !loadingRunDetails.has(runId)) {
      setLoadingRunDetails(prev => new Set(prev).add(runId));
      try {
        const res: any = await fetchTestRunById(runId);
        setRunDetails(prev => ({ ...prev, [runId]: res?.run ?? null }));
      } catch { /* ignore */ }
      finally {
        setLoadingRunDetails(prev => { const n = new Set(prev); n.delete(runId); return n; });
      }
    }
  }, [runDetails, loadingRunDetails]);


  useEffect(() => {
    fetchHistory(page);
  }, [page, fetchHistory]);

  const runSuite = useCallback((pattern: string): Promise<void> => {
    return new Promise(async (resolve) => {
      setSuiteStates(prev => ({
        ...prev,
        [pattern]: {
          status: "running",
          tests: prev[pattern].tests.map(t => ({ ...t, status: "idle" as const, durationMs: 0 })),
          durationMs: 0,
          ranAt: prev[pattern].ranAt,
          passed: undefined,
          failed: undefined,
          total: undefined,
        },
      }));

      let res: any;
      try {
        res = await runTestSuite(pattern);
      } catch {
        setSuiteStates(prev => ({ ...prev, [pattern]: { ...prev[pattern], status: "failed" } }));
        return resolve();
      }

      if (!res?.runId) {
        setSuiteStates(prev => ({ ...prev, [pattern]: { ...prev[pattern], status: "failed" } }));
        return resolve();
      }

      activeRunIdRef.current = res.runId;
      const token = localStorage.getItem("token");
      const es = new EventSource(`/api/test-suites/stream/${res.runId}?token=${token}`);

      es.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);

          if (event.type === "test_result" && event.testName) {
            setSuiteStates(prev => {
              const suite = prev[pattern];
              const idx = suite.tests.findIndex(t => t.name === event.testName);
              const entry: TestEntry = {
                name: event.testName,
                status: event.status ?? "running",
                durationMs: event.durationMs ?? 0,
              };
              const tests = idx >= 0
                ? suite.tests.map((t, i) => i === idx ? entry : t)
                : [...suite.tests, entry];
              return { ...prev, [pattern]: { ...suite, tests } };
            });
          }

          if (event.type === "complete" || event.type === "error") {
            setSuiteStates(prev => {
              const suite = prev[pattern];
              const hasFailed = suite.tests.some(t => t.status === "failed");
              const finalStatus = event.type === "error" || hasFailed ? "failed" : "passed";
              const totalDuration = suite.tests.reduce((sum, t) => sum + t.durationMs, 0);
              const passed = event.passed ?? suite.tests.filter(t => t.status === "passed").length;
              const failed = event.failed ?? suite.tests.filter(t => t.status === "failed").length;
              const total = event.total ?? suite.tests.length;
              // Discard pre-populated placeholders that were never matched by test_result events.
              // Unmatched entries stay "idle" (name mismatch between DB fullName and verbose title).
              const ranTests = suite.tests.filter(t => t.status !== "idle");
              const tests = ranTests.length > 0
                ? ranTests
                : suite.tests.map(t => ({ ...t, status: "passed" as const }));
              return {
                ...prev,
                [pattern]: {
                  ...suite,
                  status: finalStatus,
                  tests,
                  durationMs: totalDuration,
                  ranAt: new Date().toISOString(),
                  passed,
                  failed,
                  total,
                },
              };
            });
            activeRunIdRef.current = null;
            es.close();
            resolve();
          }
        } catch {
          // ignore malformed events
        }
      };

      es.onerror = () => {
        setSuiteStates(prev => ({ ...prev, [pattern]: { ...prev[pattern], status: "failed" } }));
        activeRunIdRef.current = null;
        es.close();
        resolve();
      };
    });
  }, []);

  useEffect(() => { suiteStatesRef.current = suiteStates; }, [suiteStates]);

  useEffect(() => {
    if (wasRunningRef.current && !isAnySuiteRunning && lastRunPatterns.length > 0) {
      const states = suiteStatesRef.current;
      const total = lastRunPatterns.reduce((sum, p) => sum + (states[p]?.total ?? states[p]?.tests.length ?? 0), 0);
      const passed = lastRunPatterns.reduce((sum, p) => sum + (states[p]?.passed ?? states[p]?.tests.filter(t => t.status === "passed").length ?? 0), 0);
      const failed = lastRunPatterns.reduce((sum, p) => sum + (states[p]?.failed ?? states[p]?.tests.filter(t => t.status === "failed").length ?? 0), 0);
      const durationMs = lastRunPatterns.reduce((sum, p) => sum + (states[p]?.durationMs ?? 0), 0);
      setCommittedSummary({ total, passed, failed, durationMs });
    }
    wasRunningRef.current = isAnySuiteRunning;
  }, [isAnySuiteRunning, lastRunPatterns]);


  const handleRunAll = useCallback(async () => {
  setLastRunPatterns([...ALL_PATTERNS]);

  setSuiteStates(prev => {
    const next = { ...prev };
    for (const p of ALL_PATTERNS) {
      next[p] = {
        status: "running",
        tests: prev[p].tests.map(t => ({ ...t, status: "idle" as const, durationMs: 0 })),
        durationMs: 0,
        ranAt: prev[p].ranAt,
        passed: undefined,
        failed: undefined,
        total: undefined,
      };
    }
    return next;
  });

  let res: any;
  try {
    res = await runAllTestSuites();
  } catch {
    setSuiteStates(prev => {
      const next = { ...prev };
      for (const p of ALL_PATTERNS) {
        if (next[p].status === "running") next[p] = { ...next[p], status: "failed" };
      }
      return next;
    });
    return;
  }

  if (!res?.runId) return;

  activeRunIdRef.current = res.runId;
  const token = localStorage.getItem("token");
  const es = new EventSource(`/api/test-suites/stream/${res.runId}?token=${token}`);

  es.onmessage = (e) => {
    try {
      const event = JSON.parse(e.data);
      const { pattern } = event;

      // Final signal — all suites done
      if (event.type === "complete" && !pattern) {
        activeRunIdRef.current = null;
        es.close();
        fetchHistory(page);
        return;
      }

      if (!pattern) return;

      if (event.type === "test_result" && event.testName) {
        setSuiteStates(prev => {
          const suite = prev[pattern];
          if (!suite) return prev;
          const idx = suite.tests.findIndex(t => t.name === event.testName);
          const entry: TestEntry = {
            name: event.testName,
            status: event.status ?? "running",
            durationMs: event.durationMs ?? 0,
          };
          const tests = idx >= 0
            ? suite.tests.map((t, i) => i === idx ? entry : t)
            : [...suite.tests, entry];
          return { ...prev, [pattern]: { ...suite, tests } };
        });
      }

      if (event.type === "complete" || event.type === "error") {
        setSuiteStates(prev => {
          const suite = prev[pattern];
          if (!suite) return prev;
          const hasFailed = suite.tests.some(t => t.status === "failed");
          const finalStatus = event.type === "error" || hasFailed ? "failed" : "passed";
          const passed = event.passed ?? suite.tests.filter(t => t.status === "passed").length;
          const failed = event.failed ?? suite.tests.filter(t => t.status === "failed").length;
          const total = event.total ?? suite.tests.length;
          const ranTests = suite.tests.filter(t => t.status !== "idle");
          const tests = ranTests.length > 0
            ? ranTests
            : suite.tests.map(t => ({ ...t, status: "passed" as const }));
          return {
            ...prev,
            [pattern]: {
              ...suite,
              status: finalStatus,
              tests,
              durationMs: suite.tests.reduce((sum, t) => sum + t.durationMs, 0),
              ranAt: new Date().toISOString(),
              passed,
              failed,
              total,
            },
          };
        });
      }
    } catch { /* ignore */ }
  };

  es.onerror = () => {
    setSuiteStates(prev => {
      const next = { ...prev };
      for (const p of ALL_PATTERNS) {
        if (next[p].status === "running") next[p] = { ...next[p], status: "failed" };
      }
      return next;
    });
    activeRunIdRef.current = null;
    es.close();
  };
}, [fetchHistory, page]);


  const handleCancel = async () => {
    const runId = activeRunIdRef.current;
    if (runId) {
      try {
        await cancelTestRun(runId);
      } catch {}
    }
    setSuiteStates(prev => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (next[key].status === "running") next[key] = { ...next[key], status: "failed" };
      }
      return next;
    });
    activeRunIdRef.current = null;
    fetchHistory(page);
  };

  const lastCompleted = history.find(r => r.status === "passed" || r.status === "failed");
  const totalTests = committedSummary?.total ?? 0;
  const totalPassed = committedSummary?.passed ?? 0;
  const totalFailed = committedSummary?.failed ?? 0;
  const successRate = totalTests > 0 ? `${Math.round((totalPassed / totalTests) * 100)}%` : "—";
  const duration = committedSummary && committedSummary.durationMs > 0
    ? `${(committedSummary.durationMs / 1000).toFixed(1)}s`
    : "—";
  const pageCount = Math.ceil(historyTotal / PAGE_LIMIT);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1 }}>
          {lastCompleted && (
            <Typography variant="caption" color="text.secondary">
              Last run: {new Date(lastCompleted.startedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
        {isAnySuiteRunning ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            onClick={handleCancel}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
          >
            Cancel Run
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleRunAll}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
          >
            Run All Tests
          </Button>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); if (v === "history") fetchHistory(page); }} sx={{ mb: 2 }}>
        <Tab label="Tests" value="tests" sx={{ textTransform: "none", fontWeight: 600 }} />
        <Tab label="Run History" value="history" sx={{ textTransform: "none", fontWeight: 600 }} />
      </Tabs>

      {activeTab === "tests" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {TEST_MODULES.map((module) => (
            <Paper key={module.name} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
                <Typography fontWeight={700}>{module.name}</Typography>
              </Box>
              <Divider />
              {module.suites.map((suite, i) => {
                const state = suiteStates[suite.pattern];
                const passedCount = state.passed ?? state.tests.filter(t => t.status === "passed").length;
                const failedCount = state.failed ?? state.tests.filter(t => t.status === "failed").length;
                const totalCount = state.total ?? state.tests.length;
                const hasResults = state.status !== "idle" && totalCount > 0;
                return (
                  <Box key={suite.pattern}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, flexWrap: "wrap", gap: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", cursor: state.tests.length > 0 ? "pointer" : "default", flex: 1 }}
                        onClick={() => state.tests.length > 0 && setCollapsedSuites(prev => ({ ...prev, [suite.pattern]: !prev[suite.pattern] }))}
                      >
                        <ExpandMoreIcon sx={{
                          fontSize: 18,
                          color: "text.secondary",
                          transform: collapsedSuites[suite.pattern] ? "rotate(-90deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                          visibility: state.tests.length > 0 ? "visible" : "hidden",
                        }} />
                        <Typography variant="body2" fontWeight={600}>{suite.label}</Typography>
                        {hasResults && (
                          <Chip label={`${passedCount}/${totalCount}`} size="small" color={failedCount > 0 ? "error" : "success"} />
                        )}
                        {hasResults && state.durationMs > 0 && (
                          <Typography variant="caption" color="text.secondary">{(state.durationMs / 1000).toFixed(1)}s</Typography>
                        )}
                        {state.ranAt && (
                          <Typography variant="caption" color="text.secondary">Last run: {new Date(state.ranAt).toLocaleString()}</Typography>
                        )}
                      </Box>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={state.status === "running" ? <CircularProgress size={12} color="inherit" /> : <PlayArrowIcon />}
                          onClick={() => { setLastRunPatterns([suite.pattern]); runSuite(suite.pattern); }}
                          disabled={isAnySuiteRunning}
                          sx={{ textTransform: "none", borderRadius: "6px", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
                        >
                          Run
                        </Button>
                      </Box>
                      <Collapse in={!collapsedSuites[suite.pattern]}>
                        {state.tests.length > 0 && (
                          <Table size="small" sx={{ mb: 1 }}>
                            <TableBody>
                              {state.tests.map((test, idx) => (
                                <TableRow key={idx}>
                                  <TableCell width={32}>{statusIcon(test.status)}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{test.name}</Typography>
                                  </TableCell>
                                  <TableCell align="right" width={80}>
                                    <Typography variant="caption" color="text.secondary">
                                      {test.durationMs > 0 ? `${test.durationMs}ms` : "—"}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Collapse>
                      {i < module.suites.length - 1 && <Divider />}
                  </Box>

                );
              })}
            </Paper>
          ))}
        </Box>
      )}

      {activeTab === "history" && (
        <>
          {totalTests > 0 && (
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              <SummaryCard label="Total Tests" value={totalTests} />
              <SummaryCard label="Passed" value={totalPassed} color="#22c55e" />
              <SummaryCard label="Failed" value={totalFailed} color={totalFailed > 0 ? "#ef4444" : undefined} />
              <SummaryCard label="Success Rate" value={successRate} color={totalFailed === 0 ? "#22c55e" : "#f59e0b"} />
              <SummaryCard label="Duration" value={duration} />
            </Box>
          )}
          <Typography variant="subtitle2" fontWeight={600} mb={1}>Run History</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                  <TableCell width={32} />
                  <TableCell>#</TableCell>
                  <TableCell>Triggered By</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Suites</TableCell>
                  <TableCell>Tests</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingHistory ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : history.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary" variant="body2">No runs yet. Click "Run All Tests" to start.</Typography></TableCell></TableRow>
                ) : (
                  history.map((run: any) => (
                    <React.Fragment key={run._id}>
                      <TableRow
                        hover
                        sx={{ cursor: "pointer", "& td": { borderBottom: expandedRunIds.has(run._id) ? "none" : undefined } }}
                        onClick={() => toggleRun(run._id)}
                      >
                        <TableCell>
                          <ExpandMoreIcon sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            transform: expandedRunIds.has(run._id) ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s",
                          }} />
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>#{run.runNumber}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{run.triggeredBy}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{new Date(run.startedAt).toLocaleString()}</Typography></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{run.durationMs != null ? `${(run.durationMs / 1000).toFixed(1)}s` : "—"}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {run.suites?.length === 1
                              ? run.suites[0].pattern
                              : run.suites?.length === 0 && run.pattern
                                ? run.pattern
                                : `All (${run.suites?.length ?? 0})`}
                          </Typography>
                        </TableCell>
                        <TableCell>{suiteCell(run.suites)}</TableCell>
                        <TableCell>{statusChip(run.status)}</TableCell>
                      </TableRow>

                      {expandedRunIds.has(run._id) && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, bgcolor: "#f8fafc" }}>
                            {loadingRunDetails.has(run._id) ? (
                              <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={20} />
                              </Box>
                            ) : (
                              <Table size="small">
                                <TableBody>
                                  {(runDetails[run._id]?.suites ?? []).map((suite: any) => {
                                    const isMultiSuite = (runDetails[run._id]?.suites?.length ?? 0) > 1;
                                    const suiteKey = `${run._id}:${suite.pattern}`;
                                    const isSuiteCollapsed = collapsedSuiteKeys.has(suiteKey);
                                    const testRows = suite.tests?.map((test: any, idx: number) => (
                                      <TableRow
                                        key={idx}
                                        sx={{ bgcolor: test.status === "failed" ? "#fef2f2" : "background.paper", verticalAlign: "top" }}
                                      >
                                        <TableCell width={48} />
                                        <TableCell width={32} sx={{ pt: 1.5 }}>{statusIcon(test.status)}</TableCell>
                                        <TableCell colSpan={4}>
                                          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{test.name}</Typography>
                                          {test.errorMessage && (
                                            <Typography variant="caption" component="pre" sx={{ color: "#b91c1c", whiteSpace: "pre-wrap", display: "block", mt: 0.5, fontSize: "0.72rem" }}>
                                              {test.errorMessage}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="right" sx={{ pt: 1.5 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {test.durationMs > 0 ? `${test.durationMs}ms` : "—"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell />
                                      </TableRow>
                                    ));
                                    return (
                                      <React.Fragment key={suite.pattern}>
                                        {isMultiSuite && (
                                          <TableRow
                                            sx={{ bgcolor: "#f1f5f9", cursor: "pointer" }}
                                            onClick={() => toggleSuiteKey(suiteKey)}
                                          >
                                            <TableCell colSpan={8} sx={{ py: 0.5, px: 2 }}>
                                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <ExpandMoreIcon sx={{
                                                  fontSize: 16,
                                                  color: "text.secondary",
                                                  transform: isSuiteCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                                                  transition: "transform 0.2s",
                                                }} />
                                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                  {suite.suiteName}
                                                </Typography>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                        {isMultiSuite ? (
                                          <TableRow>
                                            <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                                              <Collapse in={!isSuiteCollapsed}>
                                                <Table size="small"><TableBody>{testRows}</TableBody></Table>
                                              </Collapse>
                                            </TableCell>
                                          </TableRow>
                                        ) : testRows}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}

    </Box>
  );
};

export default UnitTestsPanel;
